/**
 * Base label dictionary for the whole app.
 * Keys are flat and dot-namespaced so a future i18n layer can consume them as-is:
 *   entity.*  -> domain nouns (singular/plural). Overriding these renames the concept everywhere.
 *   field.*   -> table column headers and form field labels shared across screens.
 *   title.*   -> page/section titles.
 *   action.*  -> buttons and commands.
 *   nav labels resolve to entity.*.plural so one override renames tab, titles and tables together.
 *
 * Per-deployment overrides live in GotaSettings.terminology (admin: Configuración > Terminología)
 * and win over these values at runtime. An unknown key renders the key itself, never blank.
 */
export const BASE_LABELS = {
	// --- Domain entities ---
	'entity.account': 'Cuenta',
	'entity.account.plural': 'Cuentas',
	'entity.account.debtor': 'Deudor',
	'entity.account.debtor.plural': 'Deudores',
	'entity.account.creditor': 'Acreedor',
	'entity.account.creditor.plural': 'Acreedores',
	'entity.debt': 'Deuda',
	'entity.debt.plural': 'Deudas',
	'entity.credit': 'Pasividad',
	'entity.credit.plural': 'Pasividades',
	'entity.contract': 'Alquiler',
	'entity.contract.plural': 'Alquileres',
	'entity.asset': 'Activo Fijo',
	'entity.asset.plural': 'Activos fijos',
	'entity.assetSale': 'Venta',
	'entity.assetSale.plural': 'Ventas',
	'entity.due': 'Cuota',
	'entity.due.plural': 'Cuotas',
	'entity.payment': 'Pago',
	'entity.payment.plural': 'Pagos',
	'entity.bill': 'Gasto',
	'entity.bill.plural': 'Gastos',
	'entity.contact': 'Contacto',
	'entity.contact.plural': 'Contactos',
	'entity.warranty': 'Garantía',
	'entity.warranty.plural': 'Garantías',
	'entity.tenant': 'Inquilino',
	'entity.assetOwner': 'Propietario',
	'entity.purchaser': 'Comprador',
	'entity.concept': 'Concepto',
	'entity.report.plural': 'Reportes',
	'entity.prediction.plural': 'Previsiones',

	// --- Navigation / dashboard ---
	'nav.dashboard': 'Inicio',
	'nav.reports': 'Reportes',
	'nav.predictions': 'Previsiones',

	// --- Page / section titles ---
	'title.confirmDelete': 'Confirmar Eliminación',
	'title.debtConceptConfig': 'Configuración de conceptos de deuda',
	'title.debtConceptConfigDescription': 'Elegí qué conceptos de deuda mostrar y en qué orden.',
	'title.creditConceptConfig': 'Configuración de conceptos de pasividad',
	'title.creditConceptConfigDescription': 'Elegí qué conceptos de pasividad mostrar y en qué orden.',
	'title.tabConfig': 'Configuración de pestañas',
	'title.tabConfigDescription': 'Elegí qué pestañas mostrar en el navegador.',
	'title.contractTemplates': 'Plantillas de Contrato',

	// --- Shared field / column labels ---
	'field.name': 'Nombre',
	'field.type': 'Tipo',
	'field.currency': 'Moneda',
	'field.folder': 'Carpeta',
	'field.amount': 'Monto',
	'field.dueAmount': 'Monto cuota',
	'field.date': 'Fecha',
	'field.email': 'Email',
	'field.address': 'Dirección',
	'field.phone': 'Teléfono',
	'field.description': 'Descripción',
	'field.observations': 'Observaciones',
	'field.status': 'Estado',
	'field.id': 'ID',
	'field.capital': 'Capital',
	'field.endDate': 'Fecha vto',
	'field.expiration': 'Vto',
	'field.expirationDate': 'Vencimiento',
	'field.periodicity': 'Periodicidad',
	'field.interest': 'Interes',
	'field.interestPercent': '% Interés',
	'field.amortization': 'Amortización',
	'field.balance': 'Saldo',
	'field.withBalance': 'Con saldo',
	'field.settled': 'Saldada',
	'field.surcharge': 'Recargo',
	'field.unpaidAmount': 'Monto pendiente',
	'field.paid': 'Pago?',
	'field.active': 'Activo',
	'field.price': 'Precio/Valor',
	'field.ubication': 'Ubicación',
	'field.guaranteedAmount': 'Monto garantizado',
	'field.totalGuaranteed': 'Total Garantizado',
	'field.multipleLots': 'Multiples padrones?',
	'field.lot': 'Padron',
	'field.lotUpper': 'Padrón',
	'field.periodInit': 'Inicio',
	'field.periodEnd': 'Fin',
	'field.closeDate': 'Fecha Finalización',
	'field.previousBalance': 'Saldo Anterior',
	'field.previousBalanceDate': 'Fecha de saldo Anterior',
	'field.previousDebt': 'Deuda Anterior',
	'field.previousDebtDate': 'Fecha de Deuda Anterior',
	'field.details': 'detalles',
	'field.paymentMethod': 'Método de pago',
	'field.lastAdjustmentDate': 'Fecha último ajuste',
	'field.lastAdjustmentIpc': 'Porcentaje último ajuste',
	'field.lastPaymentDate': 'Fecha ultimo pago',
	'field.accountConcept': 'Cuenta:Concepto',
	'field.totalDebts': 'Total Deudas',
	'field.initialPayment': 'Pago Inicial',
	'field.option': 'Opción',
	'field.debtCurrency': 'Moneda Deuda',
	'field.assetCurrency': 'Moneda Activo',
	'field.contract': 'Contrato',
	'field.property': 'Propiedad',
	'field.companyCommission': 'Comision Inmobiliaria',
	'field.rut': 'RUT',
	'field.documentType': 'Tipo de documento',
	'field.documentNumber': 'Nro. de documento',
	'field.bankAccountUSD': 'Cuenta Bancaria USD',
	'field.bankAccountUYU': 'Cuenta Bancaria UYU',
	'field.irpf': 'IRPF',

	// --- Actions ---
	'action.save': 'Guardar',
	'action.saveChanges': 'Guardar cambios',
	'action.downloadPdf': 'Descargar PDF',
	'action.export': 'Exportar',
	'action.sendEmail': 'Enviar Email',
	'action.sendWhatsapp': 'Enviar WhatsApp',
	'action.cancel': 'Cancelar',
	'action.search': 'Buscar',
	'action.restore': 'Restaurar',
	'action.add': 'Agregar',
	'action.applySurcharge': 'Aplicar Recargo',
	'action.back': 'Atrás',
	'action.backToDebt': 'Volver a la deuda',
	'action.backToContract': 'Volver al alquiler',
	'action.backToAccount': 'Volver a la cuenta',
	'action.backToAsset': 'Volver al activo',
	'action.backToAssetSale': 'Volver a la venta',
	'action.calculate': 'Calcular',
	'action.confirm': 'Confirmar',
	'action.confirmUsers': 'Confirmar usuarios',
	'action.delete': 'Eliminar',
	'action.edit': 'Editar',
	'action.editStartDate': 'Editar fecha de inicio',
	'action.goToAssetSale': 'Ir a venta de activo',
	'action.goToContract': 'Ir a alquiler',
	'action.goToGenericContract': 'Ir a contrato',
	'action.refinanceDebt': 'Refinanciar deuda',
	'action.upload': 'Subir',
	'action.backPlain': 'Atras',
	'action.changeStartDate': 'Cambiar Fecha de inicio',
	'action.uploadLower': 'subir',
	'action.viewDetails': 'Ver Detalles',
	'action.viewReceipt': 'Ver Comprobante',

	// --- Extra field / column labels ---
	'field.lastCount': 'Último conteo',
	'field.monthTotal': 'Total del mes',
	'field.options': 'Opciones',
	'field.percentage': 'Porcentaje',
	'field.provisionalAmount': 'Monto provisorio',
	'field.creditDue': 'Cuota Pasividad',
	'field.debtDue': 'Cuota Deuda',
	'field.finishDate': 'Fecha de finalizacion',
	'field.linkedCredit': 'Pasividad vinculada',
	'field.previousCredit': 'Crédito Anterior',
	'field.previousCreditPlain': 'Credito Anterior',

	// --- Form labels / placeholders ---
	'form.billingType': 'Tipo de facturación',
	'form.billingType.fixed': 'Monto fijo',
	'form.billingType.perUser': 'Por usuario',
	'form.comprobante': 'Comprobante',
	'form.contractCloseDate': 'Fecha Finalizacion de Contrato',
	'form.debtStartDate': 'Fecha de inicio de deuda',
	'form.debtStartDateLong': 'Fecha de inicio de la deuda',
	'form.detail': 'Detalle',
	'form.dueMonth': 'mes de cuota',
	'form.endDate': 'Fecha de fin',
	'form.existingDataAction': 'Que hacer con los Datos existentes:',
	'form.firstDueDay': 'Día vencimiento de 1er cuota:',
	'form.firstDueDayLong': 'Día de vencimiento de primer cuota:',
	'form.generateFrom': 'Generar a partir de:',
	'form.hasPreviousDebt': 'Tiene deuda anterior?',
	'form.hasPreviousDebtQ': '¿Tiene deuda anterior?',
	'form.inHowManyMonths': 'En cuantos meses:',
	'form.initialUserCount': 'Cantidad inicial de usuarios',
	'form.interestPercent': 'Interes %',
	'form.lastConfirmedUserCount': 'Última cantidad confirmada de usuarios',
	'form.markPreviousDuesPaid': 'Marcar cuotas previas como pagas',
	'form.no': 'No',
	'form.numMonths': 'Ingrese en cuantos meses:',
	'form.originalAmount': 'Monto Original',
	'form.paidBy': 'Quien realiza el pago',
	'form.paidQ': 'Paga?',
	'form.paymentMedium': 'Medio de pago',
	'form.percentage': 'Porcentaje (%)',
	'form.periodicityMonths': 'Periodicidad en meses',
	'form.placeholder.payerName': 'Nombre del pagador',
	'form.placeholder.paymentMedium': 'Ej: Efectivo, Transferencia',
	'form.placeholder.searchAccount': 'Buscar Cuenta... ',
	'form.placeholder.searchAssetSale': 'Buscar una venta de activo fijo...',
	'form.placeholder.searchContract': 'Buscar un alquiler...',
	'form.placeholder.searchContractGeneric': 'Buscar un contrato...',
	'form.previousDebtDate': 'Fecha deuda anterior',
	'form.pricePerUser': 'Precio por usuario',
	'form.surchargeType': 'Tipo de Recargo',
	'form.userCountMonth': 'Cantidad de usuarios del mes',
	'form.yes': 'Sí',
	'form.chooseCurrency': 'Elige Moneda',
	'form.chooseLinkedCredit': 'Elige la Pasividad vinculada',
	'form.creditStartDate': 'Fecha de inicio de credito',
	'form.creditStartDateAccent': 'Fecha de inicio de crédito',
	'form.creditStartDateLa': 'Fecha de inicio de la crédito',
	'form.creditStartDateLong': 'Fecha de inicio del crédito',
	'form.hasPreviousCredit': 'Tiene crédito anterior?',
	'form.hasPreviousCreditPlain': 'Tiene credito anterior?',
	'form.placeholder.searchCredit': 'Buscar pasividad...',
	'form.placeholder.searchDebt': 'Buscar deuda...',
	'form.placeholder.searchDue': 'Buscar cuota...',
	'form.previousCreditDate': 'Fecha crédito anterior',
	'form.previousCreditDatePlain': 'Fecha credito anterior',

	// --- User-facing messages / toasts ---
	'msg.accountLoadFailed': 'No se pudo obtener cuenta/s',
	'msg.allDuesGenerated': 'Todas las cuotas fueron creadas',
	'msg.assetSaleCapitalExceeded': 'Error. La sumatoria del capital inicial y las deudas registradas no puede superar el valor de la venta del inmueble.',
	'msg.assetSaleLoadFailed': 'No se pudo obtener venta/s',
	'msg.calculatedSurcharge': 'Recargo calculado',
	'msg.completeFields': 'Debe completar el/los campo/s',
	'msg.conceptsLoadFailed': 'No se pudieron cargar los conceptos',
	'msg.confirmCountBeforePayment': 'Confirme la cantidad de usuarios del mes antes de registrar cobros',
	'msg.confirmDeleteDebt': 'Seguro desea eliminar deuda? Si elimina deuda, seran eliminados todos los pagos relacionados',
	'msg.confirmDeletePayment': 'Seguro desea eliminar el pago?',
	'msg.confirmDeleteAccount': 'Seguro desea eliminar la cuenta?',
	'msg.confirmDeleteContact': 'Seguro desea eliminar el contacto?',
	'msg.confirmDeleteDocument': 'Seguro desea eliminar este documento?',
	'msg.confirmDeleteEvent': '¿Eliminar el evento "{title}"?',
	'msg.confirmDeleteInterest': 'Seguro desea eliminar el interes?',
	'msg.confirmDeleteUser': 'Seguro desea eliminar el Usuario?',
	'msg.confirmDeleteCollection': 'Seguro desea eliminar el cobro?',
	'msg.confirmDeleteCommission': '¿Seguro desea eliminar esta comisión?',
	'msg.contractLoadFailed': 'No se pudo obtener contrato/s',
	'msg.debtCreated': 'Deuda creada',
	'msg.debtCreatedWithPreviousDues': 'Ésta deuda fue creada con fecha anterior, por lo que fueron generadas las cuotas previas a la fecha de creación',
	'msg.debtDeleted': 'Deuda Eliminada',
	'msg.debtExpired': 'Deuda vencida',
	'msg.debtStartDateTooltip': 'Es la fecha de inicio de la deuda, si usted selecciona una fecha anterior al día de hoy se crearán las cuotas pasadas, si no es lo que desea seleccione una fecha igual o mayor a la de hoy.',
	'msg.debtStartDateUpdated': 'Fecha de inicio de deuda actualizada correctamente',
	'msg.debtUpdated': 'Deuda actualizada Correctamente',
	'msg.dueAlreadyPaid': 'Esta cuota ya está pagada',
	'msg.dueCreated': 'Cuota creada',
	'msg.duePendingUntilCount': 'Cada cuota queda pendiente hasta confirmar la cantidad de usuarios del mes.',
	'msg.editDatesTooltip': 'Estos datos solo se pueden editar luego de creada la deuda mediante la opción:Cambiar fecha de inicio.',
	'msg.enterCapitalInterest': 'Debe ingresar Capital e interés',
	'msg.enterCapitalInterestDues': 'Debe ingresar Capital, interés y cuotas',
	'msg.enterDetail': 'Debe indicar detalle.',
	'msg.expirationBeforeDate': 'La fecha de vencimiento no puede ser menor a la fecha seleccionada',
	'msg.fileUploaded': 'Archivo subido',
	'msg.firstExpiration': 'Primer Vencimiento',
	'msg.invalidAccount': 'Cuenta invalida',
	'msg.invalidContract': 'Alquiler invalido',
	'msg.invalidUserCount': 'Ingrese una cantidad de usuarios válida',
	'msg.invalidValue': 'Debe ingresar un valor válido',
	'msg.maxAvailableCapital': 'Capital máximo disponible',
	'msg.maximum': 'Máximo',
	'msg.nextExpirationTemplate': 'Próximo vencimiento {date}, cuota #{number} a generarse el {generationDate}',
	'msg.noContractAssigned': 'No tiene Contrato asignado',
	'msg.noData': 'No hay datos',
	'msg.noDebts': 'Sin deudas',
	'msg.noDebtorAssigned': 'No tiene Deudor asignado',
	'msg.noPendingCountDues': 'No hay cuotas pendientes de confirmación.',
	'msg.offSystemTooltip': 'Los datos que se completan aqui son únicamente informativos, con el fin de registrar la contabilidad fuera del sistema',
	'msg.paymentCreated': 'Cobro creado',
	'msg.paymentDeleted': 'Pago Eliminado',
	'msg.previousDuesWarning': 'Usted está por crear una deuda con fecha anterior a la fecha de hoy, si no desea crear cuotas previas debe ingresar una fecha igual o posterior al día de hoy',
	'msg.provisionalMonthlyAmount': 'Monto mensual provisorio',
	'msg.selectAccount': 'Debe seleccionar una cuenta.',
	'msg.selectAmount': 'Debe seleccionar monto.',
	'msg.selectDate': 'Debe seleccionar fecha.',
	'msg.selectDebtor': 'Debe seleccionar deudor.',
	'msg.selectExpiration': 'Debe seleccionar vencimiento.',
	'msg.selectFile': 'Debe seleccionar un archivo',
	'msg.selectPaid': 'Debe seleccionar pago.',
	'msg.selectSurchargeType': 'Debe seleccionar un tipo de recargo',
	'msg.surchargeApplied': 'Recargo aplicado correctamente',
	'msg.userCountConfirmed': 'Cantidad de usuarios confirmada',
	'msg.accountNotSelectedOrInvalid': 'Cuenta no seleccionada o invalida',
	'msg.addPaymentRequiredFields': 'Para ingresar pago debe completar los campos obligatorios: Pasividad,Cuota, Monto y Fecha',
	'msg.amountGreaterThanZero': 'Debe ingresar un monto mayor a 0',
	'msg.confirmDeleteCredit': 'Seguro desea eliminar pasividad? Si elimina pasividad, seran eliminados todos los cobros relacionados',
	'msg.confirmDeleteWarranty': 'Seguro desea eliminar garantia?',
	'msg.creditCreated': 'Pasividad creada',
	'msg.creditCreatedWithPreviousDues': 'Ésta crédito fue creado con fecha anterior, por lo que fueron generadas las cuotas previas a la fecha de creación',
	'msg.creditDeleted': 'Pasividad Eliminada',
	'msg.creditDuesLoadFailed': 'No se pudo cargar las cuotas de la pasividad',
	'msg.creditEditDatesTooltip': 'Estos datos solo se pueden editar luego de creado el credito mediante la opción:Cambiar fecha de inicio.',
	'msg.creditExpired': 'Pasividad vencida',
	'msg.creditPaymentCreated': 'Pago creado',
	'msg.creditPaymentUpdated': 'Pago actualizado correctamente',
	'msg.creditPaymentUpdatedCap': 'Pago actualizado Correctamente',
	'msg.creditStartDateTooltip': 'Es la fecha de inicio del crédito, si usted selecciona una fecha anterior al día de hoy se crearán las cuotas pasadas, si no es lo que desea seleccione una fecha igual o mayor a la de hoy.',
	'msg.creditStartDateUpdated': 'Fecha de inicio de credito actualizada correctamente ',
	'msg.creditUpdated': 'Pasividad actualizada Correctamente',
	'msg.editPaymentRequiredFields': 'Para editar pago debe completar los campos obligatorios: Monto y Fecha',
	'msg.enterCapitalDues': 'Debe ingresar Capital y cuotas',
	'msg.fileUploadedCap': 'Archivo Subido',
	'msg.invalidCredit': 'Pasividad invalida',
	'msg.invalidDebt': 'Deuda invalida',
	'msg.invalidDue': 'Cuota invalida',
	'msg.invalidShort': 'invalido',
	'msg.noLinkedCredit': 'No tiene Pasividad vinculada',
	'msg.paymentCreateFailed': 'Error al crear pago',
	'msg.paymentLoadFailed': 'No se pudo obtener pago',
	'msg.paymentUpdateFailed': 'Error al actualizar pago',
	'msg.previousDuesCreditWarning': 'Usted está por crear un crédito con fecha anterior a la fecha de hoy, si no desea crear cuotas previas debe ingresar una fecha igual o posterior al día de hoy',
	'msg.selectCredit': 'Debe seleccionar una pasividad.',
	'msg.selectCreditNoDot': 'Debe seleccionar una pasividad',
	'msg.selectDateFull': 'Debe seleccionar una fecha',
	'msg.selectDue': 'Debe seleccionar una cuota',
	'msg.selectFileShort': 'Seleccione Archivo',
	'msg.warrantyDeleted': 'Garantia Eliminada',

	// --- Extra page / section titles ---
	'title.accountingInfo': 'Información contable',
	'title.activity': 'Actividad',
	'title.changeHistory': 'Historial de cambios',
	'title.collections': 'Cobros',
	'title.confirmUserCount': 'Confirmar cantidad de usuarios',
	'title.debtWithPreviousDues': 'Deuda con cuotas previas',
	'title.deleteDebt': 'Eliminar Deuda',
	'title.deleteAccount': 'Eliminar Cuenta',
	'title.deleteContact': 'Eliminar Contacto',
	'title.deleteDocument': 'Eliminar Documento',
	'title.deleteUser': 'Eliminar Usuario',
	'title.deleteInterest': 'Eliminar Interés',
	'title.deletePayment': 'Eliminar Pago',
	'title.deleteCommission': 'Eliminar Comisión',
	'title.details': 'Detalles',
	'title.documents': 'Documentos',
	'title.attachments': 'Adjuntos',
	'title.reports': 'Informes',
	'title.dueGenerationData': 'Datos para la generación de cuotas',
	'title.editPayment': 'Editar Cobro',
	'title.interestList': 'Lista de Intereses',
	'title.newPayment': 'Nuevo Cobro',
	'title.offSystemRecord': 'Registro fuera del sistema',
	'title.paymentRegistered': 'Cobro registrado',
	'title.pendingCountDues': 'Cuotas por confirmar',
	'title.registerPayment': 'Registrar cobro',
	'title.changeHistoryLower': 'historial de cambios',
	'title.creditWithPreviousDues': 'Crédito con cuotas previas',
	'title.creditWithPreviousDuesPlain': 'Credito con cuotas previas',
	'title.deleteCredit': 'Eliminar Pasividad',
	'title.editStartDate': 'Editar Fecha de inicio',
	'title.newWarranty': 'Nueva Garantía',

	// --- Contracts area (chunk 3) ---
	'action.continue': 'Continuar',
	'action.goBack': 'Volver',
	'action.update': 'Actualizar',
	'field.collectionMethod': 'Forma de cobro',
	'field.housingUnit': 'Unidad',
	'field.pendingDue': 'Cuota pendiente',
	'field.previousBalanceDatePlain': 'Fecha saldo anterior',
	'field.previousBalanceLower': 'saldo Anterior',
	'field.responsible': 'Responsable',
	'form.hasPreviousBalance': 'Tiene saldo anterior?',
	'form.isRental': 'Es alquiler?',
	'form.paymentAccount': 'Cuenta para pago',
	'form.paymentTo': 'Pagar a',
	'form.placeholder.searchContractShort': 'Buscar alquiler...',
	'form.placeholder.searchProperty': 'Buscar una propiedad...',
	'form.placeholder.searchResponsibleUser': 'Buscar usuario responsable...',
	'form.placeholder.searchTenant': 'Buscar Inquilino... ',
	'msg.assetLoadFailed': 'No se pudo obtener activo/s',
	'msg.changeTenantTooltip': 'Para cambiar el inquilino debe finalizar el contrato, y generar uno nuevo a partir del actual',
	'msg.confirmDeleteDebtShort': 'Seguro desea eliminar la deuda?',
	'msg.contractCreated': 'Contrato creado',
	'msg.contractUpdated': 'Contrato actualizado',
	'msg.contractUpdatedCap': 'Contrato actualizado Correctamente',
	'msg.invalidProperty': 'Propiedad invalida',
	'msg.noContractsFound': 'No se encontraron alquileres',
	'msg.noContractsWithPendingDues': 'Sin alquileres con cuotas pendientes.',
	'msg.noPendingDuesForContract': 'Sin cuotas pendientes para este alquiler.',
	'msg.noPendingDuesFound': 'No se encontraron cuotas pendientes',
	'msg.noPropertyAssigned': 'No tiene Propiedad asignada',
	'msg.noResponsibleAssigned': 'No tiene Responsable asignado',
	'msg.noTenantAssigned': 'No tiene Inquilino asignado',
	'msg.notAssignedFem': 'No asignada',
	'msg.notSpecified': 'No especificado',
	'msg.rentalLoadFailed': 'No se pudo obtener el alquiler',
	'msg.selectProperty': 'Debe seleccionar una propiedad',
	'msg.selectTenantAccount': 'Debe seleccionar una cuenta como inquilino',
	'title.addPayment': 'Agregar cobro',
	'title.contractData': 'Datos Contractuales',
	'title.contractDataAlt': 'Datos Contracturales',

	// --- Assets / Bills / AssetSales / Detail screens (chunk 4) ---
	'entity.assetSentence': 'Activo fijo',
	'entity.assetSaleOf': 'Venta de activo',
	'title.saleTo': 'Venta a',
	'action.activatePeriodicity': 'Activar periodicidad',
	'action.addCollection': 'Agregar Cobro',
	'action.confirmPayment': 'Confirmar Pago',
	'action.generateContract': 'Generar Contrato',
	'action.stopPeriodicity': 'Detener periodicidad',
	'action.viewEscritura': 'Ver Escritura',
	'action.viewReceiptLower': 'ver comprobante',
	'field.idShort': 'Id',
	'field.initialDelivery': 'Entrega Inicial',
	'field.period': 'Periodo',
	'field.descriptionPlain': 'Descripcion',
	'field.document': 'Documento',
	'field.sold': 'Vendido?',
	'field.escritura': 'Escritura',
	'field.ubicationPlain': 'Ubicacion',
	'field.billingDay': 'Dia de facturacion',
	'field.billResponsible': 'Responsable del gasto',
	'field.linkedAsset': 'Activo Vinculado',
	'field.linkedAssetLower': 'Activo vinculado',
	'field.paidFull': 'Pagado',
	'field.pendingPayment': 'Pendiente',
	'field.startDate': 'Fecha de inicio',
	'field.totalInDebts': 'Total en Deudas',
	'form.billDate': 'Fecha de gasto',
	'form.chooseAsset': 'Seleccione Activo',
	'form.closeDate': 'Fecha de finalización',
	'form.chooseOwner': 'Elige al Propietario',
	'form.generateRetroactive': 'Generar gastos retroactivos',
	'form.monthYear': 'Mes/año:',
	'form.multipleLotsQ': 'El activo tiene multiples padrones?',
	'form.partyType.tenant': 'Inquilino/Comprador',
	'form.partyType.owner': 'Propietario/Vendedor',
	// Display labels for stored enum values (the values themselves never change)
	'enum.partyType.inquilino': 'inquilino',
	'enum.partyType.propietario': 'propietario',
	'enum.paymentTo.cliente': 'Cliente',
	'enum.paymentTo.inmobiliaria': 'Inmobiliaria',
	'form.placeholder.billName': 'Nombre del gasto',
	'form.placeholder.searchAsset': 'Buscar Activo...',
	'form.placeholder.searchAssetSpace': 'Buscar Activo... ',
	'form.placeholder.searchBankAccount': 'Buscar cuenta bancaria...',
	'form.select': 'Seleccionar',
	'form.selectAsset': 'Seleccionar activo',
	'form.selectAssociatedAccount': 'Seleccionar cuenta asociada',
	'form.selectCurrency': 'Seleccionar moneda',
	'form.selectMonth': 'Seleccionar mes',
	'form.selectYear': 'Seleccionar año',
	'form.startMonth': 'Mes de inicio',
	'form.startYear': 'Año de inicio',
	'form.year': 'Año:',
	'form.placeholder.assetName': 'Nom Fe activo',
	'form.placeholder.assetUbication': 'Ubicación del activo',
	'form.placeholder.folderName': 'Nombre de la carpeta',
	'form.placeholder.lotNumber': 'Número de padrón',
	'form.placeholder.searchOwnerAccount': 'Buscar cuenta de propietario...',
	'form.placeholder.searchOwnerAccountCap': 'Buscar Cuenta de propietario... ',
	'msg.assetCreated': 'Activo creado',
	'msg.assetCreateFailed': 'Error al crear activo',
	'msg.assetDeleted': 'Activo Eliminado',
	'msg.assetEditFailed': 'Error al editar Activo',
	'msg.confirmDeleteAsset': 'Seguro desea eliminar el activo? Si elimina el activo, seran eliminados todos los gastos relacionados',
	'msg.assetUpdated': 'Activo actualizado Correctamente',
	'msg.fileUploadFailed': 'Error al subir el archivo',
	'msg.assetNotSelectedInvalid': 'Activo no seleccionado invalido',
	'msg.assetNotSelectedOrInvalid': 'Activo no seleccionado o invalido',
	'msg.assetSaleCreated': 'Venta de activo creada',
	'msg.assetSaleUpdated': 'Venta de activo actualizada',
	'msg.assetSaleDeleted': 'Venta Eliminada',
	'msg.assetSaleDeletedFull': 'Venta de activo Eliminada',
	'msg.assetSaleDeleteFailed': 'Error al eliminar venta',
	'msg.assetSaleDeleteFailedFull': 'Error al eliminar venta de activo',
	'msg.confirmDeleteAssetSale': 'Seguro desea eliminar la venta de activo? Si elimina la venta, serán eliminadas todas las deudas relacionadas',
	'msg.billCreated': 'Gasto creado',
	'msg.billDeleted': 'Gasto eliminado',
	'msg.billDeletedCap': 'Gasto Eliminado',
	'msg.billPeriodicityActivated': 'La periodicidad del gasto ha sido activada.',
	'msg.billPeriodicityStopped': 'La periodicidad del gasto ha sido detenida.',
	'msg.billUpdated': 'Gasto actualizado',
	'msg.confirmActivateBillPeriodicity': '¿Está seguro que desea activar la periodicidad de este gasto?',
	'msg.confirmStopBillPeriodicity': '¿Está seguro que desea detener la periodicidad de este gasto?',
	'msg.confirmedLower': 'confirmado',
	'msg.noLower': 'no',
	'msg.completeLotField': 'Debe completar el campo padrón.',
	'msg.confirmDeleteBill': 'Seguro desea eliminar el gasto?',
	'msg.closeContractWarning': 'La fecha seleccionada es menor a la actual, al confirmar ésta fecha de finalización se cancelaran todas las cuotas y los pagos vinculados, generados con posterioridad al mes seleccionado.',
	'msg.confirmDeleteContract': 'Seguro desea eliminar el contrato?',
	'msg.contractClosed': 'Contrato finalizado Correctamente',
	'msg.contractClosedWithDate': 'Contrato Finalizado con fecha',
	'msg.contractDeleted': 'Contrato Eliminado',
	'msg.selectAtLeastOneContract': 'Debe seleccionar al menos un alquiler',
	'msg.contractsAdjusted': 'Montos actualizados',
	'msg.contractsClosed': 'Alquileres finalizados',
	'msg.enterAdjustmentValue': 'Debe ingresar el valor del ajuste',
	'msg.enterCloseDate': 'Debe ingresar la fecha de finalización',
	'msg.creditorDeleteFailed': 'Error al eliminar acreedor',
	'msg.debtListLoadFailed': 'No se pudo obtener lista de deudas',
	'msg.documentAvailable': 'Documento disponible',
	'msg.fileUploadedSuccess': 'Archivo subido con exito',
	'msg.noAssetAssigned': 'No tiene Activo asignado',
	'msg.noDocument': 'No hay documento',
	'msg.noPurchaserAssigned': 'No tiene Comprador asignado',
	'msg.generateRetroactiveTooltip': 'Si está activo, se crean los gastos de meses anteriores (desde el mes de inicio hasta hoy). Si no, solo se crea el gasto del mes actual.',
	'msg.paymentConfirmed': 'Pago Confirmado',
	'msg.invalidAsset': 'Activo invalido',
	'msg.listLoadFailed': 'No se pudo obtener lista',
	'msg.stopFutureBillGeneration': 'Detener generacion de proximos gastos',
	'msg.multipleUnitsTooltip': 'Para multiples unidades de un mismo padrón, puede dejar vacio el campo y completarlo en la venta/alquiler',
	'msg.noOwnerAssigned': 'No tiene Propietario asignado',
	'msg.noRelatedAsset': 'No tiene Activo Relacionado',
	'msg.yesPlain': 'Si',
	'msg.selectAsset': 'Debe seleccionar un activo.',
	'msg.selectOwner': 'Debe seleccionar un propietario.',
	'msg.selectPeriod': 'Debe seleccionar un periodo.',
	'msg.selectPeriodicity': 'Debe seleccionar periodicidad.',
	'title.addAsset': 'Agregar Activo',
	'title.assetSale': 'Venta de activo',
	'title.balance': 'Balance',
	'title.closeContractWarning': 'Advertencia Finalizar Contrato',
	'title.commissions': 'Comisiones',
	'title.confirmCloseContract': 'Confirmar Finalizar Contrato',
	'title.deleteAsset': 'Eliminar Activo',
	'title.deleteAssetSale': 'Eliminar Venta',
	'title.deleteBill': 'Eliminar Gasto',
	'title.deleteContract': 'Eliminar Contrato',
	'title.editAsset': 'Editar Activo',
	'title.generatedBillsInfo': 'Informacion sobre gastos generados',
	'title.paymentList': 'Lista de Pagos',

	// Settings area (sidebar groups, items, landing page)
	'settings.title': 'Configuración',
	'settings.homeDescription': 'Administrá la configuración del sistema por área.',
	'settings.searchPlaceholder': 'Buscar en configuración...',
	'settings.noResults': 'Sin resultados.',
	'settings.backToApp': 'Volver al sistema',
	'settings.signOut': 'Cerrar sesión',
	'settings.group.company': 'Empresa',
	'settings.group.business': 'Negocio',
	'settings.group.accounting': 'Contabilidad',
	'settings.group.users': 'Usuarios',
	'settings.group.integrations': 'Integraciones',
	'settings.group.tools': 'Herramientas',
	'settings.group.terminology': 'Terminología',
	'settings.groupDesc.company': 'Identidad de la empresa, tema visual y emails de notificación.',
	'settings.groupDesc.business': 'Preset, conceptos, pestañas y dashboard.',
	'settings.groupDesc.terminology': 'Renombrá los textos de la aplicación por categoría.',
	'settings.groupDesc.accounting': 'IRPF, comisiones y cuentas bancarias de la empresa.',
	'settings.groupDesc.users': 'Alta, edición y roles de los usuarios del sistema.',
	'settings.groupDesc.integrations': 'Conexiones con servicios externos.',
	'settings.groupDesc.tools': 'Mantenimiento, auditoría y plantillas.',
	'settings.item.companyInfo': 'Información de la empresa',
	'settings.item.colors': 'Tema y colores',
	'settings.item.emails': 'Emails de notificación',
	'settings.item.preset': 'Preset de negocio',
	'settings.item.terminology': 'Terminología / Etiquetas',
	'settings.item.termEntities': 'Conceptos del negocio',
	'settings.item.termTitles': 'Títulos de pantalla',
	'settings.item.termFields': 'Campos y columnas',
	'settings.item.termForms': 'Formularios',
	'settings.item.termActions': 'Botones y acciones',
	'settings.item.termMessages': 'Mensajes',
	'settings.item.termAdmin': 'Panel de configuración',
	'settings.termEntitiesDescription':
		'Renombrar una entidad la actualiza en pestañas, títulos y tablas a la vez.',
	'settings.item.dashboard': 'Configuración del Dashboard',
	'settings.item.users': 'Usuarios',
	'settings.item.googleCalendar': 'Google Calendar',
	'settings.item.esignature': 'Firma electrónica',
	'settings.item.facturacion': 'Facturación electrónica',
	'settings.item.duplicateCleanup': 'Limpieza de cuotas duplicadas',
	'settings.item.auditTracking': 'Seguimiento de modificaciones',
	'settings.cleanup.warning':
		'Herramienta de mantenimiento: elimina cuotas duplicadas de forma permanente. Ejecutá primero la vista previa y revisá el resultado antes de confirmar.',

	// Concept manager (create/edit concepts with behavior metadata)
	'concept.behaviorTitle': 'Comportamiento de los conceptos',
	'concept.behaviorDescription':
		'Cada concepto factura según un comportamiento del sistema. Crear un concepto nuevo lo habilita en los editores y en la generación automática de cuotas.',
	'concept.newConcept': 'Nuevo concepto',
	'concept.editTitle': 'Editar concepto',
	'concept.created': 'Concepto creado',
	'concept.updated': 'Concepto actualizado',
	'concept.nameRequired': 'El nombre del concepto es requerido',
	'concept.systemBadge': 'Sistema',
	'concept.systemBehaviorLocked':
		'Concepto del sistema: su comportamiento está protegido y no puede modificarse.',
	'concept.keyFixedNote': 'La clave no cambia al renombrar:',
	'concept.behaviorChangeTitle': 'Cambiar comportamiento',
	'concept.behaviorChangeWarning':
		'El cambio de comportamiento afecta solo la generación futura de cuotas; los registros existentes conservan el comportamiento con el que fueron creados.',
	'concept.commissionNote':
		'Opcional: porcentaje usado al registrar comisiones para este concepto (si se omite, se usa el % configurado en Contabilidad).',
	'concept.field.name': 'Nombre',
	'concept.field.key': 'Clave',
	'concept.field.behavior': 'Comportamiento',
	'concept.field.flags': 'Opciones',
	'concept.field.defaultPeriodicity': 'Periodicidad por defecto',
	'concept.field.commissionPercentage': '% Comisión',
	'concept.flag.requiresContract': 'Requiere contrato',
	'concept.flag.requiresAsset': 'Requiere activo',
	'concept.flag.requiresAssetSale': 'Requiere venta',
	'concept.flag.treatAsExpense': 'Es gasto',
	'concept.flag.ipcAdjustable': 'Ajusta por IPC',
	'concept.flag.allowsPerUser': 'Permite por usuario',
	'concept.behavior.periodic': 'Periódico',
	'concept.behavior.amortizable': 'Amortizable',
	'concept.behavior.interestOnly': 'Solo interés',
	'concept.behavior.installmentSale': 'Venta a plazo',
	'concept.behavior.deposit': 'Depósito / Garantía',
	'concept.behavior.accountDelivery': 'Entrega a cuenta',
	'concept.behavior.oneOff': 'Puntual (sin cuotas)',

	// E-signature (DocuSeal integration)
	'esignature.send': 'Enviar a firmar',
	'esignature.refresh': 'Actualizar estado',
	'esignature.status.pending': 'Enviado a firmar',
	'esignature.status.completed': 'Firmado',
	'esignature.status.declined': 'Rechazado',
	'esignature.status.expired': 'Vencido',
	'esignature.modal.title': 'Enviar a firmar',
	'esignature.modal.selectOrCreateIntro':
		'Elegí una plantilla ya creada en DocuSeal para este documento, o creá una nueva a partir del archivo actual.',
	'esignature.modal.prepareButton': 'Crear plantilla nueva',
	'esignature.modal.templateInstructions':
		'En la pestaña de DocuSeal: agregá el segundo firmante si hace falta, asigná cada campo al firmante correcto y guardá la plantilla. Importante: no uses el botón "Send" ni compartas el enlace desde DocuSeal — volvé a esta ventana y continuá acá, para que el envío quede vinculado a este documento.',
	'esignature.modal.reopenLink': 'Volver a abrir DocuSeal',
	'esignature.modal.selectTemplate': 'Plantilla creada en DocuSeal',
	'esignature.modal.refreshTemplates': 'Actualizar lista',
	'esignature.modal.loading': 'Cargando...',
	'esignature.modal.signersDescription': 'Completá los datos de cada firmante.',
	'esignature.modal.signersRequired': 'Completá nombre y email de todos los firmantes',
	'esignature.modal.sent': 'Documento enviado a firmar',
	'esignature.modal.generatedPrompt': 'Documento generado. ¿Enviarlo a firmar ahora?',
	'esignature.modal.autoSignedHint': 'Se firmará automáticamente con la firma guardada de la empresa',
	'esignature.settings.statusTitle': 'Estado',
	'esignature.settings.defaultSignerTitle': 'Firmante por defecto',
	'esignature.settings.configured': 'Configurado',
	'esignature.settings.notConfigured': 'No configurado',
	'esignature.settings.reachable': 'DocuSeal accesible',
	'esignature.settings.unreachable': 'DocuSeal no responde',
	'esignature.settings.setupInstructions':
		'Configurá DOCUSEAL_URL, DOCUSEAL_API_KEY y DOCUSEAL_WEBHOOK_SECRET en las variables de entorno del servidor (ver docs/esignature.md).',
	'esignature.settings.defaultSignerName': 'Nombre del firmante de la empresa',
	'esignature.settings.defaultSignerEmail': 'Email del firmante de la empresa',
	'esignature.settings.saved': 'Configuración guardada',
	'esignature.settings.signatureImageTitle': 'Firma de la empresa',
	'esignature.settings.signatureImageHelp':
		'Subí una imagen de la firma de la empresa (PNG o JPG). Cuando esté configurada, el firmante de la empresa se marca como firmado automáticamente al enviar el documento — solo el cliente necesita firmar para completarlo.',
	'esignature.settings.uploadSignature': 'Subir firma',
	'esignature.settings.signatureSaved': 'Firma guardada',
	'esignature.settings.howItWorksTitle': 'Cómo funciona',
	'esignature.settings.howItWorksBody':
		'Al enviar un documento a firmar se abre DocuSeal para confirmar los campos de firma; luego ambas partes reciben un email con el enlace para firmar. El estado se actualiza automáticamente al completarse.',
	'esignature.settings.webhookInstructions':
		'Webhook: configurá en DocuSeal (Settings > Webhooks) la URL "<tu-app>/api/esignature/webhook" con el header X-Gota-Webhook-Secret y los eventos form.completed, form.declined, submission.completed, submission.expired.',
	'esignature.link.title': 'Vincular firma existente',
	'esignature.link.description':
		'Si el envío se hizo directamente desde DocuSeal (sin volver al asistente de este sistema), elegilo de la lista para vincularlo a este documento y traer su estado actual.',
	'esignature.link.empty': 'No se encontraron envíos recientes en DocuSeal',
	'esignature.link.template': 'Plantilla',
	'esignature.link.signers': 'Firmantes',
	'esignature.link.statusHeader': 'Estado',
	'esignature.link.linkButton': 'Vincular',
	'esignature.link.linked': 'Firma vinculada',

	// Facturación electrónica (GotaBilling / CFE DGI integration)
	'facturacion.facturar': 'Facturar',
	'facturacion.refresh': 'Actualizar estado',
	'facturacion.status.borrador': 'Borrador (sin emitir)',
	'facturacion.status.confirmado': 'Emitido',
	'facturacion.status.anulado': 'Anulado',
	'facturacion.tipoCfe.101': 'e-Ticket (101)',
	'facturacion.tipoCfe.111': 'e-Factura (111)',
	'facturacion.modal.title': 'Facturar balance',
	'facturacion.modal.intro':
		'Se creará un borrador de comprobante en GotaBilling con los ingresos del mes. La emisión (folio y CAE) se confirma después desde GotaBilling.',
	'facturacion.modal.receptor': 'Receptor',
	'facturacion.modal.descripcion': 'Descripción',
	'facturacion.modal.cantidad': 'Cantidad',
	'facturacion.modal.precioUnitario': 'Precio unitario',
	'facturacion.modal.total': 'Total',
	'facturacion.modal.confirm': 'Crear borrador',
	'facturacion.modal.sent': 'Borrador creado en GotaBilling',
	'facturacion.panel.title': 'Facturación electrónica',
	'facturacion.panel.serieNumero': 'Serie y número',
	'facturacion.panel.cae': 'CAE',
	'facturacion.panel.motivo': 'Motivo de anulación',
	'facturacion.panel.openDraft': 'Confirmar en GotaBilling',
	'facturacion.panel.pdf': 'Descargar PDF',
	'facturacion.panel.facturarNuevamente': 'Facturar nuevamente',
	'facturacion.panel.refreshed': 'Estado actualizado',
	'facturacion.iva.basica': 'IVA básica (22%)',
	'facturacion.iva.minima': 'IVA mínima (10%)',
	'facturacion.iva.exento': 'Exento de IVA',
	'facturacion.iva.default': 'Usar predeterminado de la empresa',
	'facturacion.formaPago.contado': 'Contado',
	'facturacion.formaPago.credito': 'Crédito',
	'facturacion.account.sectionTitle': 'Facturación electrónica',
	'facturacion.account.ivaOverride': 'IVA para facturación',
	'facturacion.account.ciudad': 'Ciudad (receptor CFE)',
	'facturacion.settings.statusTitle': 'Estado',
	'facturacion.settings.defaultsTitle': 'Valores por defecto',
	'facturacion.settings.configured': 'Configurado',
	'facturacion.settings.notConfigured': 'No configurado',
	'facturacion.settings.reachable': 'GotaBilling accesible',
	'facturacion.settings.unreachable': 'GotaBilling no responde',
	'facturacion.settings.testConnection': 'Probar conexión',
	'facturacion.settings.setupInstructions':
		'Configurá GOTABILLING_URL, GOTABILLING_API_KEY y GOTABILLING_WEBHOOK_SECRET en las variables de entorno del servidor (ver docs/facturacion.md). La API key se crea en GotaBilling, en Configuración > API.',
	'facturacion.settings.indicadorFacturacion': 'IVA por defecto',
	'facturacion.settings.montosBrutos': 'Los precios incluyen IVA (montos brutos)',
	'facturacion.settings.formaPago': 'Forma de pago por defecto',
	'facturacion.settings.saved': 'Configuración guardada',
	'facturacion.settings.howItWorksTitle': 'Cómo funciona',
	'facturacion.settings.howItWorksBody':
		'Desde la pestaña Balance de una cuenta, contrato o venta, el botón "Facturar" crea un borrador de CFE en GotaBilling con los ingresos del mes (las deducciones del balance no se incluyen). El folio y CAE se asignan al confirmar la emisión en GotaBilling, y el estado vuelve automáticamente por webhook.',
	'facturacion.settings.webhookInstructions':
		'Webhook: en GotaBilling, editá la API key y configurá como Webhook URL:',
};

// Nav tab schema -> terminology key. A terminology override on the mapped key
// takes precedence over the ObjectTab label so renaming an entity renames its tab too.
export const NAV_LABEL_KEYS = {
	dashboard: 'nav.dashboard',
	accounts: 'entity.account.plural',
	debts: 'entity.debt.plural',
	credits: 'entity.credit.plural',
	contracts: 'entity.contract.plural',
	assets: 'entity.asset.plural',
	assetSales: 'entity.assetSale.plural',
	reports: 'nav.reports',
	predictions: 'nav.predictions',
};
